import React, {useEffect, useMemo} from "react";

import {
  ActionResult,
  EducationSessionResult,
  Challenge,
  ChallengeMode,
  ChallengeInputMode,
  Maybe,
  Scalars,
} from "../../schema";
import {PlayableAction} from "../../redux/player/types";
import {Scenario} from "../../types";

import SessionUserInputActionDisplay from "./session-events/SessionUserInputActionDisplay";
import SessionAvatarActionDisplay from "./session-events/SessionAvatarActionDisplay";
import SessionSystemActionDisplay from "./session-events/SessionSystemActionDisplay";
import {gql} from "@apollo/client";
import {useAudio} from "../../providers/audio";
import {AudioPlayback} from "../../libs/audio";
import {useTranslation} from "react-i18next";
import {useFormState} from "../../hooks/useFormState";
import WithTooltip from "../../ui/tooltip";
import Select from "../../ui/select";

import classes from "./SessionActionsList.module.css";
import SessionAvatarResponseDisplay from "./session-events/SessionAvatarResponseDisplay";
import InitAvatarActionDisplay from "./session-events/InitAvatarActionDisplay";

type props = {
  actionsResults: EducationSessionResult["actionResults"];
  initAvatarAction?: EducationSessionResult["initAvatarAction"];
  challenge: Pick<Challenge, "avatar" | "inputMode" | "mode">;
  actions: PlayableAction[];
  additionalScenarios?: Scenario[];
  finishedScenarios?: Maybe<Scalars["ID"]>[];
};

type formState = {
  scenarioId: string | undefined;
};

const SessionActionsList = function ({
  actionsResults,
  initAvatarAction,
  challenge,
  actions,
  additionalScenarios,
  finishedScenarios,
}: props) {
  const {t} = useTranslation();

  const indexedResults = useMemo(
    () => new Map(actionsResults.map((results) => [results.actionId, results])),
    [actionsResults],
  );

  const indexedScenarios = useMemo(
    () => new Map(additionalScenarios?.map((scenario) => [scenario.id, scenario.actions])),
    [additionalScenarios],
  );

  const mainScenario = useMemo(() => {
    return SessionActionsListScenario(actions, indexedResults, challenge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, indexedResults, finishedScenarios, challenge]);

  const {audioContext, audioManager} = useAudio();

  useEffect(() => {
    audioManager.installPlayback("results", new AudioPlayback(audioContext));
    return () => audioManager.uninstallPlayback("results");
  }, [audioContext, audioManager]);

  useEffect(
    () => () => {
      audioManager.getPlayback("results")?.abortPlay();
    },
    [audioManager],
  );

  const formState = useFormState<formState>({
    initialValues: {
      scenarioId: "",
    },
    preventDefault: true,
  });

  if (challenge.mode === ChallengeMode.RPG_MODE) {
    return (
      <>
        {initAvatarAction && (
          <InitAvatarActionDisplay
            key="init-avatar"
            initAvatarAction={initAvatarAction}
            avatar={challenge.avatar}
          />
        )}

        {actionsResults.map((result, index) => {
          const key = `${result.actionId}-${result.adaptiveInputUuid || index}`;
          const action = actions.find((a) => a.id === result.actionId);
          if (!action) return null;

          return (
            <>
              <SessionUserInputActionDisplay
                key={key}
                action={action as any}
                result={result}
                hideExpectedText={true}
                noSpeech={challenge.inputMode === ChallengeInputMode.TEXT}
                customScore={false}
                challenge={challenge}
              />
              {result.response && (
                <SessionAvatarResponseDisplay key={key} result={result} challenge={challenge} />
              )}
            </>
          );
        })}
      </>
    );
  }

  return (
    <>
      {finishedScenarios && finishedScenarios.length > 0 && (
        <form className={classes.form}>
          <WithTooltip className={classes.help} as="span" helpText={t("components.SessionActionsList.helpText")}>
            <Select
              name="scenarioId"
              value={formState.values.scenarioId ?? undefined}
              onChange={formState.changeHandler}
            >
              <option key={"main"} value={""}>
                {t("components.SessionActionsList.mainScenario")}
              </option>

              {finishedScenarios.map((id) => (
                <option
                  key={id}
                  value={additionalScenarios?.find((scenario) => scenario.id === id)!.id}
                  translate="no"
                >
                  {additionalScenarios?.find((scenario) => scenario.id === id)!.name}
                </option>
              ))}
            </Select>
          </WithTooltip>
        </form>
      )}

      {!finishedScenarios || formState.values.scenarioId === "" ? (
        <>{mainScenario}</>
      ) : (
        <>
          {SessionActionsListScenario(indexedScenarios.get(formState.values.scenarioId!)!, indexedResults, challenge)}
        </>
      )}
    </>
  );
};

const SessionActionsListScenario = function (
  actions: PlayableAction[],
  indexedResults: Map<string, ActionResult>,
  challenge: Pick<Challenge, "avatar" | "inputMode" | "mode">,
) {
  return (
    <>
      {actions.map((action) => {
        const result = indexedResults.get(action.id);
        switch (action.__typename) {
          case "UserInputAction":
          case "ChoiceUserInputAction":
            if (!indexedResults.has(action.id)) {
              //throw new Error(`User input action (id: ${action.id}) has no corresponding result`)
              return <></>;
            }

            return (
              <>
                <SessionUserInputActionDisplay
                  key={action.id}
                  action={action}
                  result={result!}
                  noSpeech={challenge.inputMode === ChallengeInputMode.TEXT}
                  customScore={
                    challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
                    challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST
                  }
                  challenge={challenge}
                />
                {result?.response && (
                  <SessionAvatarResponseDisplay key={`resp_${action.id}`} result={result} challenge={challenge} />
                )}
              </>
            );
          case "AvatarAction":
            if (indexedResults.has(action.id)) {
              throw new Error(`Avatar action (id: ${action.id}) has corresponding result`);
            }
            return <SessionAvatarActionDisplay key={action.id} action={action} avatar={challenge.avatar} />;
          case "SystemAction":
            if (indexedResults.has(action.id)) {
              throw new Error(`System action (id: ${action.id}) has corresponding result`);
            }
            return <SessionSystemActionDisplay key={action.id} action={action} />;
          default:
            throw new Error(`Trying to show result for unsupported Action type: "${action.__typename}"`);
        }
      })}
    </>
  );
};

SessionActionsList.fragments = {
  root: gql`
    fragment SessionActionsList on Action {
      id

      ...SessionUserInputActionDisplay
      ...SessionAvatarActionDisplay
      ...SessionSystemActionDisplay
    }

    ${SessionUserInputActionDisplay.fragments.root}
    ${SessionAvatarActionDisplay.fragments.root}
    ${SessionSystemActionDisplay.fragments.root}
  `,

  actionsResults: gql`
    fragment SessionActionsListActionResult on ActionResult {
      actionId
      ...SessionUserInputActionDisplayActionResult
    }

    ${SessionUserInputActionDisplay.fragments.actionResult}
  `,
};

export default SessionActionsList;
